/**
 * Copy text to clipboard with fallback methods
 * Works across all browsers and security contexts
 */
export function copyToClipboard(text: string): Promise<boolean> {
  return new Promise((resolve) => {
    // Method 1: Try fallback method first (most reliable)
    try {
      const success = fallbackCopyToClipboard(text);
      if (success) {
        resolve(true);
        return;
      }
    } catch (err) {
      console.warn('Fallback copy failed, trying Clipboard API:', err);
    }

    // Method 2: Try modern clipboard API if fallback didn't work
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => resolve(true))
        .catch((err) => {
          console.error('Clipboard API failed:', err);
          // Try one more fallback attempt
          resolve(fallbackCopyToClipboard(text));
        });
    } else {
      // Final fallback
      resolve(fallbackCopyToClipboard(text));
    }
  });
}

/**
 * Fallback method using execCommand
 * Works in all browsers without clipboard permissions
 */
function fallbackCopyToClipboard(text: string): boolean {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-999999px';
    textarea.style.top = '-999999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    
    return success;
  } catch (err) {
    console.error('Clipboard copy failed:', err);
    return false;
  }
}

/**
 * Copy to clipboard with user feedback
 * Shows prompt dialog as ultimate fallback
 */
export async function copyToClipboardWithFeedback(text: string, successMessage?: string): Promise<void> {
  const success = await copyToClipboard(text);
  
  if (success) {
    alert(successMessage || 'Copied to clipboard!');
  } else {
    // Ultimate fallback: show prompt for manual copy
    prompt('Copy this text:', text);
  }
}